// React and Third-party
import { useState, Fragment, useEffect, useCallback, useRef } from 'react';
import NumberFormat from 'react-number-format';

// Components
import TableHeadSort from './TableHeadSort';
import PaginadorParent, { paginadorFilter } from '../../../components/paginador/PaginadorParent';

// Styles (CSS Module)
import styles from '../../../css/radio.module.css';

export default function BalancesTable(props) {
    const {
        balances,
        loadingBalances,
        loadingInquilinosMovimientos,
        onMovimientosSearch,
        HistorialComponent,
        selectedInquilino,
        onSelectInquilino,
    } = props;

    const [activeInquilinoHistorial, setActiveInquilinoHistorial] = useState(0);
    const [prePagedData, setPrePagedData] = useState(balances);
    const [{ page, elementsPerPage }, setPaginadorInfo] = useState({ page: 0, elementsPerPage: 5 });
    const [keySort, setKeySort] = useState({ name: "", direction: "" });

    const pagedData = paginadorFilter(prePagedData, page, elementsPerPage);

    // Referencia al thead para medir su altura
    const theadRef = useRef(null);
    const [theadHeight, setTheadHeight] = useState(0);

    const calculateTheadHeight = useCallback(() => {
        if (theadRef.current) {
            setTheadHeight(theadRef.current.getBoundingClientRect().height);
        }
    }, []);

    useEffect(() => {
        calculateTheadHeight();
        window.addEventListener('resize', calculateTheadHeight);
        return () => window.removeEventListener('resize', calculateTheadHeight);
    }, [calculateTheadHeight]);

    useEffect(() => {
        calculateTheadHeight();
    }, [balances, calculateTheadHeight]);

    // Ordenamiento manual
    const sortByKey = (newOrder) => {
        const { name, direction } = newOrder;
        const sortedData = [...prePagedData];

        sortedData.sort((a, b) => {
            let valA = a[name];
            let valB = b[name];

            if (name === 'saldo') {
                valA = parseFloat(valA) || 0;
                valB = parseFloat(valB) || 0;
                return direction === 'asc' ? valA - valB : valB - valA;
            }

            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();

            if (direction === 'asc') {
                return valA.localeCompare(valB, 'es', { sensitivity: 'base' });
            } else {
                return valB.localeCompare(valA, 'es', { sensitivity: 'base' });
            }
        });

        setKeySort(newOrder);
        setPrePagedData(sortedData);
        setPaginadorInfo({ page: 0, elementsPerPage });
    };

    // Actualizar datos paginados cuando cambian los balances
    useEffect(() => {
        setPrePagedData(balances);
        setPaginadorInfo({ page: 0, elementsPerPage });
    }, [balances]);

    // Abrir historial de movimientos
    const openInquilinoHistorial = (id_inquilino) => {
        const idToSet = activeInquilinoHistorial !== id_inquilino ? id_inquilino : 0;
        setActiveInquilinoHistorial(idToSet);
        if (activeInquilinoHistorial === id_inquilino) return;
        onMovimientosSearch(id_inquilino);
    };

    const LoadingContent = () => (
        <tr>
            <td className="text-center" colSpan={7} style={{ height: 200 }}>
                <i className="fas fa-spinner fa-spin"></i>
            </td>
        </tr>
    );

    const EmptyContent = () => (
        <tr>
            <td className="text-center" colSpan={7} style={{ height: 200 }}>
                <h4>Sin Balances</h4>
            </td>
        </tr>
    );

    return (
        <div className="card-table card-owners tw-column" id="card-owners">
            <div className="table-scroll-container" style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <table className="full table-equal">
                    <colgroup>
                        <col width="18%" />
                        <col width="18%" />
                        <col width="18%" />
                        <col width="13%" />
                        <col width="13%" />
                        <col width="10%" />
                        <col width="10%" />
                    </colgroup>
                    <thead ref={theadRef} style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                        <tr>
                            <th className="text-left">
                                <TableHeadSort
                                    title="Residente"
                                    orderName="inquilino"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="text-left"
                                />
                            </th>
                            <th className="text-left">
                                <TableHeadSort
                                    title="Subfraccionamiento"
                                    orderName="subfraccionamiento"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="text-left"
                                />
                            </th>
                            <th className="text-left">
                                <TableHeadSort
                                    title="Vivienda"
                                    orderName="numero_registro"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="text-left"
                                />
                            </th>
                            <th className="text-right">
                                <TableHeadSort
                                    title="Balance"
                                    orderName="saldo"
                                    orderKey={keySort}
                                    onOrderKeyChange={(newOrder) => sortByKey(newOrder)}
                                    className="justify-end"
                                />
                            </th>
                            <th className="text-center">Historial</th>
                            <th className="text-center">Seleccionar</th>
                        </tr>
                    </thead>
                    <tbody className="extra-padding">
                        {loadingBalances ? (
                            <LoadingContent />
                        ) : pagedData.length === 0 ? (
                            <EmptyContent />
                        ) : (
                            <TableContent
                                content={pagedData}
                                activeInquilinoHistorial={activeInquilinoHistorial}
                                loadingMovimientos={loadingInquilinosMovimientos}
                                openInquilinoHistorial={openInquilinoHistorial}
                                HistorialComponent={HistorialComponent}
                                theadHeight={theadHeight}
                                selectedInquilino={selectedInquilino}
                                onSelectInquilino={onSelectInquilino}
                            />
                        )}
                    </tbody>
                </table>
            </div>

            {!loadingBalances && (
                <PaginadorParent
                    data={prePagedData}
                    onPagedDataChange={({ page, elementsPerPage }) => setPaginadorInfo({ page, elementsPerPage })}
                />
            )}
        </div>
    );
}

function TableContent(props) {
    const {
        content,
        activeInquilinoHistorial,
        loadingMovimientos,
        openInquilinoHistorial,
        HistorialComponent,
        theadHeight,
        selectedInquilino,
        onSelectInquilino,
    } = props;

    return content.map((inquilino, index) => (
        <Fragment key={inquilino.id_tarjeta || inquilino.id_inquilino || `row-${index}`}>
            <tr
                className={activeInquilinoHistorial === inquilino.id_inquilino ? "row-active" : undefined}
                style={
                    activeInquilinoHistorial === inquilino.id_inquilino
                        ? {
                            position: "sticky",
                            top: theadHeight,
                            backgroundColor: "#E7E7E7",
                            zIndex: 2,
                        }
                        : undefined
                }
            >
                <td className="text-left">{inquilino.propietario || inquilino.inquilino}</td>
                <td className="text-left">{inquilino.subfraccionamiento}</td>
                <td className="text-left">{inquilino.numero_registro}</td>
                <td className="text-right">
                    <NumberFormat
                        value={inquilino.saldo}
                        displayType="text"
                        thousandSeparator={true}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                    />
                </td>
                <td className="text-center">
                    <button
                        className="btn-full btn-mini btn-secondary color-white"
                        disabled={loadingMovimientos}
                        type="button"
                        onClick={() => openInquilinoHistorial(inquilino.id_inquilino)}
                    >
                        {loadingMovimientos && activeInquilinoHistorial === inquilino.id_inquilino ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i
                                className={
                                    "font-small fas fa-info-circle " +
                                    (activeInquilinoHistorial === inquilino.id_inquilino ? "color-red" : "")
                                }
                            />
                        )}
                    </button>
                </td>
                <td className="text-center">
                    <div className={styles.radioContainer}>
                        <input
                            type="checkbox"
                            name={`seleccion_${inquilino.id_inquilino}`}
                            checked={selectedInquilino?.id_inquilino === inquilino.id_inquilino}
                            onChange={(e) => {
                                // Si el checkbox está marcado, seleccionar; si no, deseleccionar
                                if (e.target.checked) {
                                    onSelectInquilino(inquilino);
                                } else {
                                    onSelectInquilino(null);
                                }
                            }}
                            className={styles.radioCheckbox}
                        />
                    </div>
                </td>
            </tr>
            {!loadingMovimientos &&
                activeInquilinoHistorial !== 0 &&
                activeInquilinoHistorial === inquilino.id_inquilino && (
                    <tr key={inquilino.id_inquilino + "mov"} className="acordeon-row" style={{ backgroundColor: "#FFF" }}>
                        <td className="acordeon" colSpan="9">
                            <HistorialComponent
                                inquilino={inquilino.inquilino}
                            />
                        </td>
                    </tr>
                )}
        </Fragment>
    ));
}